import { Injectable, EventEmitter, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { filter, Observable, of, takeUntil } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthModeService } from './oidc/auth-mode.service';
import { OidcSessionEndedError, OidcSessionService } from './oidc/oidc-session.service';
import { OidcStorageService } from './oidc/oidc-storage.service';
import { LegacySessionService } from './legacy-session.service';

export { TokenKey, RefreshKey, ExpiresKey, KeepLoggedKey } from './legacy-session.service';

export const RoleTypeTabKey = 'role_type_tab';
export const BookSupplyTypeKey = 'book_supply_type_tab';
export const JobTypeTabKey = 'job_type_tab';

export const RedirectedFromUrlKey = 'redirectedFromUrl';

@Injectable()
export class AuthService {
  private BASE_URL: string = environment.apiBaseURL;
  private headers: HttpHeaders = new HttpHeaders({
    'Accept': 'application/json',
    'Content-Type': 'application/x-www-form-urlencoded'
  });

  public LogInEvent = new EventEmitter<any>();
  public LogOutEvent = new EventEmitter<any>();

  public ThirdPartyServicesAuthenticated: boolean = false;

  private oidcSession?: OidcSessionService;
  private leavingLegacySession = false;
  
  constructor(
    private http: HttpClient,
    private injector: Injector,
    private mode: AuthModeService,
    private legacy: LegacySessionService,
  ) {}

  get oidc(): OidcSessionService {
    if (!this.oidcSession) {
      this.oidcSession = this.injector.get(OidcSessionService);
      this.oidcSession.ended$.subscribe(() => {
        this.tryLogoutMoEngage();
        this.ThirdPartyServicesAuthenticated = false;
        this.LogOutEvent.emit();
      });
      let authenticated = false;
      this.oidcSession.token$.subscribe(token => {
        const wasAuthenticated = authenticated;
        authenticated = !!token;
        if (authenticated && !wasAuthenticated && this.shouldUseOidc()) this.LogInEvent.emit();
      });
    }
    return this.oidcSession;
  }

  initialize(): Promise<void> {
    return this.shouldUseOidc() ? this.oidc.initialize() : Promise.resolve();
  }

  get tokenChanges$(): Observable<string | null> {
    return this.shouldUseOidc() ? this.oidc.token$ : of(this.token);
  }

  isOidcMode(): boolean {
    return this.shouldUseOidc();
  }

  shouldUseOidc(url?: string): boolean {
    return this.mode.useOidc(url);
  }

  get preservingLegacySession(): boolean { return this.mode.preservingLegacySession; }
  get hasValidLegacySession(): boolean { return !this.leavingLegacySession && this.legacy.valid; }

  resumeWithOidc(returnUrl = window.location.pathname + window.location.search + window.location.hash): void {
    if (this.leavingLegacySession) return;
    this.leavingLegacySession = true;
    // A different tab may already have retired this session and signed in again.
    if (this.legacy.session) this.legacy.retire();
    this.tryLogoutMoEngage();
    this.ThirdPartyServicesAuthenticated = false;
    this.LogOutEvent.emit();
    const target = new URL(returnUrl, window.location.origin);
    window.location.replace(target.origin === window.location.origin ? target.href : '/');
  }

  authorizeWithOidc(redirectedFromUrl?: string): void {
    void this.oidc.ensureAuthenticated(redirectedFromUrl || '/');
  }

  login(username, password): Promise<any> {
    const epoch = this.legacy.epoch;
    const baseUrl = `${this.BASE_URL}`.endsWith('/api') ? `${this.BASE_URL}`.slice(0, -4) : `${this.BASE_URL}`;
    const url = `${baseUrl}/token`;
    const params = new URLSearchParams('');

    params.set('username', username);
    params.set('password', password);
    params.set('client_id', environment.loginBaseConf.client_id);
    params.set('client_secret', environment.loginBaseConf.client_secret);
    params.set('grant_Type', environment.loginBaseConf.Grant_Type);

    return this.http.post(url, params.toString(), { headers: this.headers }).toPromise().then(async response => {
      if (this.legacy.epoch !== epoch) throw new OidcSessionEndedError();
      if (!this.mode.isLegacyIntegration && this.injector.get(OidcStorageService).hasSessionState) {
        await this.oidc.clearForLegacySession(epoch);
      }
      if (this.legacy.epoch !== epoch) throw new OidcSessionEndedError();
      this.legacy.save(response['access_token'], response['refresh_token'], response['.expires'], epoch);
      if (this.legacy.epoch !== epoch) throw new OidcSessionEndedError();
      this.LogInEvent.emit();
    }).catch(error => {
      if (error instanceof OidcSessionEndedError) {
        throw new HttpErrorResponse({ status: 401, error: {
          error: 'session_ended', error_description: 'Your sign-in attempt ended. Please try again.',
        } });
      }
      throw error;
    });
  }

  pipeLogin(token: string, refToken: string, expires: string) {
    this.legacy.save(token, refToken, expires);
  }

  set keepLoggedIn(val: boolean) {
    this.legacy.remember = val;
  }

  get keepLoggedIn(): boolean {
    return this.legacy.remember;
  }

  get expires() {
    return this.legacy.session?.expires ?? null;
  }

  get token() {
    if (this.shouldUseOidc()) return this.oidc.accessToken;
    return this.leavingLegacySession ? null : this.legacy.session?.token ?? null;
  }

  get refToken() {
    return this.legacy.session?.refresh ?? null;
  }

  refreshToken(refreshToken): Observable<any> {
    const epoch = this.legacy.epoch;
    const baseUrl = `${this.BASE_URL}`.endsWith('/api') ? `${this.BASE_URL}`.slice(0, -4) : `${this.BASE_URL}`;
    const url = `${baseUrl}/token`;
    const params = new URLSearchParams();

    params.set('refresh_token', refreshToken);
    params.set('client_id', environment.loginBaseConf.client_id);
    params.set('client_secret', environment.loginBaseConf.client_secret);
    params.set('grant_type', 'refresh_token');

    return this.http.post(url, params.toString(), { headers: this.headers }).pipe(
      filter(() => this.legacy.epoch === epoch),
      takeUntil(this.LogOutEvent),
    );
  }

  logout(): boolean {
    const useOidc = this.shouldUseOidc();
    if (this.mode.isLegacyIntegration) this.legacy.clear();
    else this.legacy.retire();
    if (useOidc) {
      this.oidc.logout();
      return true;
    }
    
    localStorage.removeItem(RoleTypeTabKey);
    localStorage.removeItem(BookSupplyTypeKey);
    localStorage.removeItem(JobTypeTabKey);
    localStorage.removeItem(RedirectedFromUrlKey);
    
    this.tryLogoutMoEngage();
    this.ThirdPartyServicesAuthenticated = false;

    this.LogOutEvent.emit();

    if (!this.mode.isLegacyIntegration &&
        (this.preservingLegacySession || this.injector.get(OidcStorageService).hasSessionState)) {
      if (!this.leavingLegacySession) {
        this.leavingLegacySession = true;
        void this.oidc.clearForLegacySession().then(() => window.location.assign('/login'), () => {
          console.warn('[Auth] Stored OIDC session cleanup failed.');
          window.location.assign('/login');
        });
      }
      return true;
    }

    return false;
  }

  isLoggedIn(): boolean {
    if (this.isOidcMode()) {
      return this.oidc.authenticated;
    }
    
    return this.hasValidLegacySession;
  }

  resetPassword(email): Observable<any> {
    const url = `${this.BASE_URL}/account/resetpasswordcustom`;
    let params = new HttpParams();

    params = params.append('email', email);

    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), params: params };

    return this.http.get(url, options).pipe(result => result);
  }

  changePassword(email, password, token): Observable<any> {
    const url = `${this.BASE_URL}/account/setpasswordforcode`;
    const jsonHeaders = new HttpHeaders({ 'Content-Type': 'application/json' });
    const params = {
      'Email': email,
      'Password': password,
      'Token': token
    };

    return this.http.post(url, params, { headers: jsonHeaders }).pipe(result => result);
  }

  tokenValid(email, token): Observable<any> {
    const url = `${this.BASE_URL}/account/passwordresettokenisvalid`;
    let params = new HttpParams();

    params = params.append('email', email);
    params = params.append('token', token);

    const options = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), params: params };

    return this.http.get(url, options).pipe(result => result);
  }

  tryLogoutMoEngage() {
    try {
      (window as any).Moengage.destroy_session();
    }
    catch(e) {}
  }
}
